import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
  BarChart,
  Bar,
} from "recharts";
import { TrendingUp, ArrowUpRight, ArrowDownRight, Minus, FileDown } from "lucide-react";

const fmtMoney = (n) =>
  `$ ${(Number(n) || 0).toLocaleString("es-MX", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const fmtInt = (n) => (Number(n) || 0).toLocaleString("es-MX");

const RANGES = [
  { label: "6M", value: 6 },
  { label: "12M", value: 12 },
  { label: "24M", value: 24 },
];

function DeltaPill({ pct, invert = false }) {
  if (pct === null || pct === undefined) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] text-[#8b7e5e]">
        <Minus size={11} /> s/d
      </span>
    );
  }
  const positive = pct >= 0;
  const good = invert ? !positive : positive;
  const color = good ? "#1f6b3a" : "#a14040";
  const Icon = positive ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] font-medium"
      style={{ color }}
    >
      <Icon size={11} />
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

function YoYCard({ title, current, previous, pct, money = true, invert = false, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#fbf7ee] border border-[#e6dcc1] px-4 py-3"
    >
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[#8b7e5e]">{title}</div>
        <DeltaPill pct={pct} invert={invert} />
      </div>
      <div className="font-serif text-xl text-[#1c1a14] mt-1">
        {money ? fmtMoney(current) : fmtInt(current)}
      </div>
      <div className="text-[11px] text-[#8b7e5e] mt-0.5">
        año anterior: {money ? fmtMoney(previous) : fmtInt(previous)}
      </div>
    </div>
  );
}

export default function TrendsChart() {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState({ series: [], yoy: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const res = await api.get("/reports/trends", { params: { months } });
        if (!alive) return;
        setData({ series: res.data?.series || [], yoy: res.data?.yoy || null });
        setError("");
      } catch (e) {
        if (!alive) return;
        setError("No se pudieron cargar las tendencias");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [months]);

  const series = data.series;
  const yoy = data.yoy;

  const exportPdf = async () => {
    try {
      const res = await api.get("/reports/dashboard.pdf", {
        params: { months },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = `panel_ejecutivo_${months}m.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError("No se pudo descargar el PDF del panel");
    }
  };

  return (
    <div
      data-testid="dashboard-trends"
      className="bg-white border border-[#d9cfb8] p-6 md:p-8"
      style={{ borderTop: "3px solid #c8a04a" }}
    >
      <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[#8b7e5e] flex items-center gap-2">
            <TrendingUp size={12} /> Tendencias
          </div>
          <h2 className="font-serif text-2xl text-[#1c1a14] mt-1">
            Crecimiento de la congregación
          </h2>
          {yoy && (
            <div className="text-[11px] text-[#8b7e5e] mt-1">
              Comparando <span className="text-[#1c1a14]">{yoy.current_label}</span> vs{" "}
              <span className="text-[#1c1a14]">{yoy.previous_label}</span>
            </div>
          )}
        </div>

        <div
          data-testid="trends-range-selector"
          className="inline-flex items-center gap-3 flex-wrap"
        >
          <div className="inline-flex border border-[#d9cfb8]">
            {RANGES.map((r) => {
              const active = months === r.value;
              return (
                <button
                  key={r.value}
                  data-testid={`trends-range-${r.value}`}
                  onClick={() => setMonths(r.value)}
                  className="px-3 py-1.5 text-xs uppercase tracking-widest transition-colors"
                  style={{
                    background: active ? "#1c2a3a" : "transparent",
                    color: active ? "#fbf7ee" : "#5d5849",
                  }}
                >
                  {r.label}
                </button>
              );
            })}
          </div>
          <button
            data-testid="export-dashboard-pdf"
            onClick={exportPdf}
            disabled={loading}
            className="inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors disabled:opacity-50"
            style={{ background: "#c8a04a", color: "#1c1a14", borderColor: "#a8842f" }}
          >
            <FileDown size={13} /> Exportar PDF
          </button>
        </div>
      </div>

      {yoy && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <YoYCard
            testid="yoy-ingresos"
            title="Ingresos (mes)"
            current={yoy.ingresos.current}
            previous={yoy.ingresos.previous}
            pct={yoy.ingresos.pct}
          />
          <YoYCard
            testid="yoy-diezmos"
            title="Diezmos (mes)"
            current={yoy.diezmos.current}
            previous={yoy.diezmos.previous}
            pct={yoy.diezmos.pct}
          />
          <YoYCard
            testid="yoy-gastos"
            title="Gastos (mes)"
            current={yoy.gastos.current}
            previous={yoy.gastos.previous}
            pct={yoy.gastos.pct}
            invert
          />
          <YoYCard
            testid="yoy-asistencia"
            title="Asistencia (mes)"
            current={yoy.asistencia.current}
            previous={yoy.asistencia.previous}
            pct={yoy.asistencia.pct}
            money={false}
          />
        </div>
      )}

      {loading ? (
        <div className="h-64 flex items-center justify-center text-sm text-[#8b7e5e]">
          Cargando…
        </div>
      ) : error ? (
        <div className="h-64 flex items-center justify-center text-sm text-[#a14040]">
          {error}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="text-[11px] uppercase tracking-widest text-[#8b7e5e] mb-2">
              Ingresos vs Gastos ({months} meses)
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 260 }}>
                <LineChart data={series} margin={{ top: 10, right: 16, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke="#ede4cc" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "#5d5849", fontSize: 11 }}
                    interval={months > 12 ? 1 : 0}
                  />
                  <YAxis
                    tick={{ fill: "#5d5849", fontSize: 11 }}
                    tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                  />
                  <Tooltip
                    formatter={(v) => fmtMoney(v)}
                    contentStyle={{
                      background: "#1c2a3a",
                      border: "1px solid #c8a04a",
                      color: "#fbf7ee",
                    }}
                    labelStyle={{ color: "#e7c879" }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11, color: "#5d5849" }} />
                  <Line
                    type="monotone"
                    dataKey="ingresos"
                    name="Ingresos"
                    stroke="#1c2a3a"
                    strokeWidth={2.5}
                    dot={{ r: 3, fill: "#1c2a3a" }}
                    activeDot={{ r: 5 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="gastos"
                    name="Gastos"
                    stroke="#a14040"
                    strokeWidth={2}
                    dot={{ r: 3, fill: "#a14040" }}
                  />
                  <Line
                    type="monotone"
                    dataKey="tithes"
                    name="Diezmos"
                    stroke="#c8a04a"
                    strokeWidth={2}
                    strokeDasharray="4 4"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#8b7e5e] mb-2">
              Asistencia mensual
            </div>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 600, height: 260 }}>
                <BarChart data={series} margin={{ top: 10, right: 8, bottom: 0, left: -20 }}>
                  <CartesianGrid stroke="#ede4cc" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: "#5d5849", fontSize: 10 }}
                    interval={months > 12 ? 1 : 0}
                  />
                  <YAxis tick={{ fill: "#5d5849", fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{
                      background: "#1c2a3a",
                      border: "1px solid #c8a04a",
                      color: "#fbf7ee",
                    }}
                    labelStyle={{ color: "#e7c879" }}
                  />
                  <Bar dataKey="attendance" name="Asistencia" fill="#1c2a3a" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
